import { useState } from 'react'
import { BankOutlined } from '@ant-design/icons'
import { Alert, Card, Col, Row, Space, Tag, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import type { TenantCandidate } from '@/shared/auth/authApi'
import { useAuth } from '@/shared/auth/useAuth'
import { AuthShell } from './AuthShell'

/** `LoginPage` bu ekranga o'tayotganda uzatadigan holat */
export interface TenantChoiceState {
  choiceToken: string
  candidates: TenantCandidate[]
  /** Tanlovdan keyin qayerga borish kerak — asl so'ralgan sahifa yoki bosh */
  from: string
}

/**
 * Kirishdan KEYINGI alohida ekran: bir nechta tashkilotga ega login uchun
 * har tashkilot — bosiladigan karta (texnik kod emas, NOM ko'rsatiladi).
 *
 * Odatda operator bitta tashkilotda ishlaydi, ya'ni bu ekran kamdan-kam
 * ko'rinadi — shuning uchun sodda va tushunarli qilib qurilgan: kod yo'q,
 * faqat nom, valyuta va biznes turi belgisi.
 */
export function TenantChoicePage() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { completeTenantChoice } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as TenantChoiceState | null

  const [pendingId, setPendingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // To'g'ridan-to'g'ri (masalan sahifa qayta yuklanib) ochilsa — oraliq
  // token yo'qolgan, qaytadan login qilish kerak
  if (!state?.choiceToken) return <Navigate to="/login" replace />

  const choose = (tenantId: string) => {
    if (pendingId) return
    setPendingId(tenantId)
    setError(null)

    void completeTenantChoice(state.choiceToken, tenantId)
      .then(() => navigate(state.from, { replace: true }))
      .catch((err: unknown) => {
        setError(translateError(err, t))
        setPendingId(null)
      })
  }

  return (
    <AuthShell title={t('auth.tenant.title')} subtitle={t('auth.tenant.subtitle')} width={520}>
      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Row gutter={[12, 12]}>
        {state.candidates.map((candidate) => (
          <Col xs={24} sm={12} key={candidate.tenantId}>
            <Card
              hoverable
              loading={pendingId === candidate.tenantId}
              onClick={() => choose(candidate.tenantId)}
              data-testid="tenant-card"
              data-tenant-code={candidate.code}
              style={{ cursor: pendingId ? 'default' : 'pointer', height: '100%' }}
            >
              <Space align="start">
                <BankOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                <div>
                  <Typography.Title level={5} style={{ marginBottom: 6 }}>
                    {candidate.name}
                  </Typography.Title>
                  <Space size={6} wrap>
                    {candidate.businessKind ? (
                      <Tag>{t(`businessKind.${candidate.businessKind}`)}</Tag>
                    ) : null}
                    {candidate.currency ? <Tag color="blue">{candidate.currency}</Tag> : null}
                  </Space>
                </div>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </AuthShell>
  )
}
